import { useMemo, useRef, useState, useEffect, useSyncExternalStore } from 'react';
import { RefChip } from '@/views/actes/RefChip';
import { newActe, onNewActe, clearNewActe } from '@/lib/actesNew';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Lang } from '@/data/types';
import { KBIcon } from '@/components/kbIcons';
import { friendlyError } from '@/lib/friendlyError';
import {
  actesDossier, actesDossierLifecycle, actesUpdateDemande, actesUpdateDossier,
  actesFileLink, actesDossierMember, actesDpcFile, actesDeleteDpc,
  actesDossierHistory, actesAttachmentsCount, actesAttachmentsZip,
  actesPosteList, actesPosteAdd, actesPosteFile, actesPosteRemove, actesUpload, actesDocumentCounts,
  type ActesReference, type ActesDpc,
} from '@/integrations/actesApi';
import { DossierModal } from './DossierModal';
import { ActeModal } from './ActeModal';
import { ParallelComplementModal } from './ParallelImport';
import { ActesAvatar, OwnerPicker, peopleReader, nameOf } from './ActesPerson';
import { DocumentPicker, DocCell } from './DocumentPicker';
import { pushUndo } from '@/lib/actesUndo';
import { DuplicateFinder } from './DuplicateFinder';
import { DocViewer } from './DocViewer';
import { ActeView } from './ActeView';
import { LinkChips } from './LinkChips';
import { DpcModal } from './DpcPanel';
import { Notes } from './Notes';
import { fmtDay, daysUntil } from './dates';
import { fetchTicketById } from '@/integrations/hubspot';
import { useTidyName, dossierName } from '@/hooks/useTidyName';
import { countryOfRef, hasFrenchBlocks, typeLabelEn, actesEn } from '@/lib/actesCountry';

/**
 * ONE DOSSIER — the client, what has been asked for, and what has come back.
 *
 * The page is the team's own, block for block, because the order encodes how
 * they work: the lifecycle first (a closed dossier is a different thing), then
 * WHO and WHERE (status, lead, team, the links out), then HOW FAR (what is
 * received and, more useful, what is still MISSING), then the requests
 * themselves — editable where they are read, because changing a status is the
 * act of the day and opening a form for it is why the spreadsheet outlived
 * two apps.
 */
export function DossierDetail({ lang, reference, id, onBack }: {
  lang: Lang; reference: ActesReference; id: string; onBack: () => void;
}) {
  const T = (fr: string, en: string) => (lang === 'fr' ? fr : actesEn(en));
  /* Portugal's page is France's without the DPC and the Poste log, and with
     the request's own words where France names an authority. */
  const country = countryOfRef(reference);
  const fr = hasFrenchBlocks(country);
  /* ── LA LIGNE QU'ON VIENT DE CRÉER ──
     A new request is one row among four hundred and looks like all of them.
     The one just made is scrolled to and flashed once, then forgotten — a
     later visit must not celebrate yesterday's work. */
  const freshId = useSyncExternalStore(onNewActe, newActe, newActe);
  const freshRow = useRef<HTMLTableRowElement | null>(null);
  useEffect(() => {
    if (!freshId || !freshRow.current) return;
    freshRow.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
    const t = window.setTimeout(clearNewActe, 2600);
    return () => window.clearTimeout(t);
  }, [freshId]);
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editActe, setEditActe] = useState<string | null>(null);
  const [completing, setCompleting] = useState(false);
  const [reason, setReason] = useState<{ what: 'close' | 'reopen'; text: string } | null>(null);
  const [zipAsk, setZipAsk] = useState<number | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [showMissing, setShowMissing] = useState(false);
  /* La ligne de « ce qu'il manque » qu'on vient de cliquer. */
  const [seed, setSeed] = useState<{ person: string; type: string } | null>(null);
  const [picker, setPicker] = useState<{ id: string; person: string; hasReceived: boolean; name: string | null } | null>(null);
  /* A document is read against the row it belongs to; a new browser tab takes
     one of the two away. */
  const [view, setView] = useState<(() => Promise<{ url: string; name: string | null; mime?: string | null }>) | null>(null);
  const [viewActe, setViewActe] = useState<string | null>(null);
  /* A complementary piece belongs to this dossier: creating, correcting,
     removing and reading one has to be possible from here. Leaving the screen
     to open the DPC tab and search for the dossier again is not the same
     thing — it is how they stop being filed at all. */
  const [editDpc, setEditDpc] = useState<ActesDpc | null | 'new'>(null);
  const [askDpc, setAskDpc] = useState<ActesDpc | null>(null);
  const [memOpen, setMemOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const q = useQuery({
    queryKey: ['actes', 'dossier', id],
    queryFn: ({ signal }) => actesDossier(id, signal),
    staleTime: 30_000,
  });
  /* THE POST. Half of what a French dossier receives is not an act at all:
     acknowledgements, recorded letters, the prefecture writing back. It lives
     with the dossier because that is where somebody looks for it. */
  const poste = useQuery({
    queryKey: ['actes', 'poste', id],
    queryFn: ({ signal }) => actesPosteList(id, signal),
    staleTime: 60_000,
    enabled: fr,
  });
  const posteInput = useRef<HTMLInputElement>(null);
  const [posteErr, setPosteErr] = useState('');

  /* How many documents each demande carries — one request for the whole
     table, so the badge on a row costs nothing. */
  const counts = useQuery<Record<string, number>>({
    queryKey: ['actes', 'doc-counts', id],
    queryFn: ({ signal }) => actesDocumentCounts((q.data?.actes ?? []).map((a) => a.id), signal),
    enabled: !!q.data?.actes?.length,
    staleTime: 60_000,
  });

  /* The case this dossier is tied to — fetched for its real name, which is
     what repairs a dossier whose stored name is the raw filing subject. */
  const tk = useQuery({
    queryKey: ['hs-ticket', q.data?.dossier.ticket_id ?? q.data?.dossier.hubspot_id ?? ''],
    queryFn: () => fetchTicketById(country, (q.data?.dossier.ticket_id ?? q.data?.dossier.hubspot_id) as string),
    enabled: !!(q.data?.dossier.ticket_id ?? q.data?.dossier.hubspot_id),
    staleTime: 5 * 60_000,
    retry: false,
  });
  const tidy = useTidyName(tk.data);

  const history = useQuery({
    queryKey: ['actes', 'history', id],
    queryFn: ({ signal }) => actesDossierHistory(id, signal),
    enabled: showHistory,
    staleTime: 30_000,
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ['actes'] });
  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true); setErr('');
    try { await fn(); await refresh(); }
    catch (e) { setErr(friendlyError(e, lang)); }
    setBusy(false);
  };

  /* What is still missing is the useful half of "how far along": a percentage
     tells you there is work left, this tells you WHICH.
     It is computed HERE, above the early returns: a hook that sits below one
     of them runs on some renders and not on others, and React takes the whole
     screen down for it (#310) — which is exactly what opening a dossier did. */
  const missing = useMemo(
    () => (q.data?.actes ?? [])
      .filter((a) => !a.is_received)
      .map((a) => ({ id: a.id, person: a.person_concerned,
        type: fr ? (a.type_short_fr || a.type_label_fr || '') : (a.request_line || typeLabelEn(reference.acteTypes, a.acte_type_id)) })),
    [q.data, fr, reference.acteTypes],
  );

  const docCounts = counts.data ?? {};

  if (q.isLoading) return <p className="muted-note">{T('Chargement…', 'Loading…')}</p>;
  if (q.error || !q.data) {
    return (
      <div className="empty-state">
        <div className="ico">📁</div>
        <h3>{T('Dossier introuvable', 'Dossier not found')}</h3>
        <p>{q.error ? friendlyError(q.error, lang) : ''}</p>
        <button className="btn btn-ghost" onClick={onBack}>{T('Retour', 'Back')}</button>
      </div>
    );
  }

  const { dossier: d, actes, members, dpc } = q.data;
  const pct = Math.round(Number(d.completion_pct) || 0);
  const canWrite = reference.me.isStaff && !d.closed_at;
  const byId = new Map(reference.profiles.map((p) => [p.id, p]));
  /* Qui est sur une demande : la principale ET celles d'à côté. */
  const onActe = peopleReader(reference.profiles);
  const fmt = (x: unknown) => fmtDay(x, lang, { day: '2-digit', month: 'short', year: '2-digit' });
  const soon = (x: unknown) => daysUntil(x) < 30 * 864e5;

  const openReceived = (acteId: string) => setView(() => () => actesFileLink(acteId));

  return (
    <section className="actes-panel v7">
      {/* 1 — Where you are, and the one thing that changes what this page IS. */}
      <div className="pillrow">
        <button className="btn" onClick={onBack}>
          <KBIcon name="back" size={13} /> {T('Retour aux dossiers', 'Back to dossiers')}
        </button>
        <span className="spacer" />
        {reference.me.isStaff && (
          <button className="btn" onClick={() => setReason({ what: d.closed_at ? 'reopen' : 'close', text: '' })}>
            {d.closed_at ? T('Rouvrir', 'Reopen') : T('Clôturer', 'Close')}
          </button>
        )}
      </div>

      {/* 2 — Who, where, and the doors out. */}
      <div className="card dh">
        {/* LE NOM RESPIRE, LES PORTES SONT À PART.
            Tout tenait sur une seule ligne — le nom, les trois liens, quatre
            boutons et le crayon — avec six pixels entre chacun : rien ne se
            lisait. Le nom et son numéro forment maintenant un bloc à gauche ;
            les liens (Drive, Parallel, HubSpot) et les actions sont deux
            groupes séparés à droite, avec un filet entre eux. */}
        <div className="dh-top">
          <div>
            {/* The stored name, repaired when it is plainly the case's filing
                subject — see dossierName(). The original is on hover. */}
            <h2 title={d.name}>{dossierName(d.name, tk.data?.subject, tidy.full)}</h2>
            {d.closed_at && <span className="pill pink">{T('Clôturé', 'Closed')}</span>}
            {d.hubspot_id && <span className="chipref" title="HubSpot">#{d.hubspot_id}</span>}
            {/* Portugal: the IRN consultation key of the case, read from HubSpot. */}
            {!fr && tk.data?.consultationKey && (
              <span className="chipref" title="IRN consultation key (from HubSpot)">Consultation key {tk.data.consultationKey}</span>
            )}
          </div>
          <div className="dh-tools">
            {/* Always all three. A link that was not set simply did not
                appear, so the only way to add one was to open the edit form
                and hunt for the field — which is why so many dossiers have no
                Drive folder recorded anywhere. */}
            <LinkChips lang={lang} dossierId={id} canWrite={canWrite}
              drive={d.drive_url} parallel={d.parallel_url} hubspot={d.hubspot_url} />
            <span className="vr" aria-hidden="true" />
            {/* The case in the portal — the tie the fusion added. */}
            {(d.ticket_id || d.hubspot_id) && (
              <button className="btn btn-s"
                onClick={() => window.dispatchEvent(new CustomEvent('lexidy:open-case', { detail: { id: d.ticket_id || d.hubspot_id } }))}>
                <KBIcon name="case" size={13} /> {T('Le dossier client', 'The case')}
              </button>
            )}
            <button className="btn btn-s" onClick={() => setShowHistory((v) => !v)}>
              <KBIcon name="clock" size={13} /> {T('Historique', 'History')}
            </button>
            <button className="btn btn-s" disabled={busy}
              onClick={() => void act(async () => setZipAsk((await actesAttachmentsCount(id)).count))}>
              <KBIcon name="download" size={13} /> {T('Tout télécharger', 'Download all')}
            </button>
            {reference.me.isStaff && (
              <button className="rb" title={T('Modifier', 'Edit')}
                onClick={() => setEditing(true)}><KBIcon name="edit" size={13} /></button>
            )}
          </div>
        </div>

        <div className="fields">
          <div className="field">
            <label>{T('Statut', 'Status')}</label>
            <select className="sel" value={d.status_id ?? ''} disabled={!reference.me.isStaff || busy}
              onChange={(e) => void act(() => actesUpdateDossier(id, { status_id: e.target.value ? Number(e.target.value) : null }))}>
              <option value="">—</option>
              {reference.dossierStatuses.map((s) => (
                <option key={s.id} value={s.id}>{lang === 'fr' ? s.label_fr : s.label_en}</option>
              ))}
            </select>
          </div>
          <div className="field">
            <label>{T('Avocat référent', 'Lead lawyer')}</label>
            <select className="sel" value={d.lead_lawyer_id ?? ''} disabled={!reference.me.isStaff || busy}
              onChange={(e) => void act(() => actesUpdateDossier(id, { lead_lawyer_id: e.target.value || null }))}>
              <option value="">{T('Sans responsable', 'No lead')}</option>
              {reference.profiles.filter((p) => p.is_lawyer).map((p) => (
                <option key={p.id} value={p.id}>{p.full_name ?? p.email}</option>
              ))}
            </select>
          </div>
          <div className="field teamfield">
            <label>{T('Équipe', 'Team')} <span className="mono">{members.length}</span></label>
            <div className="teamchips">
              {members.map((m) => (
                <span key={m.profile_id} className="tchip">
                  <ActesAvatar person={byId.get(m.profile_id)} />
                  {m.full_name ?? m.email}
                  {reference.me.isStaff && (
                    <button onClick={() => void act(() => actesDossierMember(id, m.profile_id, false, m.role))} aria-label="remove">×</button>
                  )}
                </span>
              ))}
              {reference.me.isStaff && (
                <div className="actes-pick-anchor">
                  <button className="actes-pick-add" onClick={() => setMemOpen((o) => !o)}>
                    <KBIcon name="plus" size={12} /> {T('Ajouter', 'Add')}
                  </button>
                  {memOpen && (
                    <>
                      <div className="mw-dd-scrim" onClick={() => setMemOpen(false)} />
                      <div className="actes-pick-menu">
                        {reference.profiles.filter((p) => !members.some((m) => m.profile_id === p.id)).map((p) => (
                          <button key={p.id} onClick={() => { void act(() => actesDossierMember(id, p.id, true)); setMemOpen(false); }}>
                            {p.full_name ?? p.email}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
          {d.closed_at && (
            <span className="dim">{T('Clôturé le', 'Closed on')} {fmt((d.submission_date ?? d.closed_at).slice(0, 10))}</span>
          )}
        </div>
      </div>

      {/* 3 — How far, and what is missing. */}
      {/* 3 — Combien, ce qui manque, et les gestes qui vont avec.
             Marquage de la maquette : `.progblock` avec le pourcentage à
             gauche, la grande barre au milieu, les actions à droite ; et
             `.missing` replié dessous. */}
      <div className="card" style={{ marginTop: 14 }}>
        <div className="progblock">
          <span className={`pc num ${pct < 40 ? 'low' : pct < 70 ? 'mid' : ''}`}>{pct}%</span>
          <div className="mid">
            <div className="bigbar"><i style={{ width: `${pct}%` }} /></div>
            <div className="meta">
              {T(`${d.actes_received} sur ${d.actes_total} actes reçus`, `${d.actes_received} of ${d.actes_total} records received`)}
              {missing.length > 0 && ` · ${T(`${missing.length} manquants`, `${missing.length} still missing`)}`}
            </div>
          </div>
          <div className="acts">
            {missing.length > 0 && (
              <button type="button" className="btn btn-s" aria-expanded={showMissing}
                onClick={() => setShowMissing((v) => !v)}>
                <KBIcon name="chevron" size={12} />
                {T(`Il manque ${missing.length}`, `${missing.length} still missing`)}
              </button>
            )}
            {canWrite && (
              <button className="btn btn-s" onClick={() => setCompleting(true)}>
                <KBIcon name="ai" size={13} /> {T('Compléter (Parallel)', 'Complete (Parallel)')}
              </button>
            )}
            {/* La même personne demandée deux fois est l'accident normal d'un
                dossier monté depuis un formulaire puis fini à la main. */}
            {canWrite && actes.length > 1 && <DuplicateFinder lang={lang} dossierId={id} />}
          </div>
        </div>
        {showMissing && missing.length > 0 && (
          <div className="missing">
            <div className="eyebrow" style={{ marginBottom: 10 }}>
              {T('Ce qu’il manque encore au dossier', 'What the dossier still needs')}
            </div>
            <div className="misslist">
              {missing.map((m) => (
                /* UNE LIGNE EST UNE DEMANDE QUI N'EXISTE PAS ENCORE.
                   La maquette le proposait en note ; ici elle le fait :
                   cliquer ouvre « Nouvelle demande » avec la personne et le
                   type déjà remplis. */
                <button key={m.id} type="button" className="row"
                  title={T('Créer cette demande', 'Create this request')}
                  onClick={() => setSeed({ person: m.person, type: m.type ?? '' })}>
                  <b>{m.person}</b>{m.type && <span>{m.type}</span>}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {err && <p className="muted-note ni-err">⚠ {err}</p>}

      {view && <DocViewer lang={lang} load={view} onClose={() => setView(null)} />}

      {viewActe && (() => {
        const a = actes.find((x) => x.id === viewActe);
        return a ? (
          <ActeView lang={lang} reference={reference} row={a}
            onClose={() => setViewActe(null)}
            onEdit={() => { setViewActe(null); setEditActe(a.id); }} />
        ) : null;
      })()}

      {picker && (
        <DocumentPicker lang={lang} acteId={picker.id} person={picker.person}
          hasReceived={picker.hasReceived} receivedName={picker.name} onClose={() => setPicker(null)} />
      )}

      {/* 4 — The requests. */}
      {/* CHAQUE SECTION A SA TÊTE AU-DESSUS DE SA CARTE.
          `.actes-panel.v7` est un bloc sans `gap` : les anciennes lignes
          `.actes-tabline` n'avaient aucune marge et chaque titre (et son
          bouton) se collait à la carte d'au-dessus. `.sec-h` est la tête de
          section de la maquette pour cette même page — l'air au-dessus, le
          titre à gauche, l'action à droite. */}
      <div className="sec-h">
        <h3>{T('Demandes d’acte', 'Civil-record requests')}</h3>
        {canWrite && (
          <div className="right">
            <button className="btn btn-s" onClick={() => setAdding(true)}>
              <KBIcon name="plus" size={13} /> {T('Nouvelle demande', 'New request')}
            </button>
          </div>
        )}
      </div>

      {actes.length === 0 ? (
        <p className="sec-empty">{T('Aucune demande pour l’instant.', 'No request yet.')}</p>
      ) : (
        <div className="actes-table-wrap">
          <table className="actes-table rows">
            <thead>
              <tr>
                <th className="doc" />
                <th>#</th>
                <th>{T('Personne concernée', 'Person')}</th>
                <th>{T('Type', 'Type')}</th>
                <th>{fr ? T('Autorité', 'Authority') : 'Document requested'}</th>
                <th>{T('Statut', 'Status')}</th>
                <th>{T('Resp.', 'Owner')}</th>
                <th>{T('Émission', 'Issued')}</th>
                <th>{T('Expire', 'Expires')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {actes.map((a) => (
                <tr key={a.id} ref={a.id === freshId ? freshRow : undefined}
                  className={a.id === freshId ? 'actes-fresh' : undefined}>
                  {/* The document leads the row, as it did in the old site:
                      one opens straight away, several ask which. */}
                  <td className="doc">
                    <DocCell lang={lang} total={(a.file_path ? 1 : 0) + (docCounts[a.id] ?? 0)}
                      onOpen={() => {
                        const total = (a.file_path ? 1 : 0) + (docCounts[a.id] ?? 0);
                        if (a.file_path && total === 1) void openReceived(a.id);
                        else setPicker({ id: a.id, person: a.person_concerned, hasReceived: !!a.file_path, name: a.file_name });
                      }} />
                  </td>
                  <td className="dim">{a.numero || '—'}</td>
                  <td className="actes-person">
                    <strong>{a.person_concerned}</strong>
                    <RefChip value={a.reference} lang={lang} />
                  </td>
                  <td>{(fr ? a.type_short_fr || a.type_label_fr : typeLabelEn(reference.acteTypes, a.acte_type_id)) || '—'}</td>
                  <td className="dim">{(fr ? a.authority_label_fr : a.request_line) || '—'}</td>
                  <td>
                    {/* Every inline change offers the way back: one wrong
                        click on a table row should cost a click, not a hunt. */}
                    {/* LES MÊMES COULEURS QUE DANS DEMANDES.
                        La même donnée se lisait en pastille colorée dans la
                        table des demandes et en menu gris ici : deux dessins
                        pour un seul état. C'est le menu qui porte la couleur
                        maintenant — toujours modifiable, mais reconnaissable
                        d'un coup d'œil comme ailleurs. */}
                    <span className={`actes-statuspick c-${a.status_color || 'slate'}`}>
                    <select className="actes-inline" value={a.status_id} disabled={!reference.me.isStaff || busy}
                      onChange={(e) => {
                        const was = a.status_id; const now = Number(e.target.value);
                        const label = (id: number | null) => reference.acteStatuses.find((s) => s.id === id);
                        void act(async () => {
                          await actesUpdateDemande(a.id, { status_id: now });
                          pushUndo({
                            label: T('Statut modifié', 'Status changed'),
                            detail: fr
                              ? `${a.person_concerned} · ${label(was)?.label_fr ?? '—'} → ${label(now)?.label_fr ?? '—'}`
                              : `${a.person_concerned} · ${label(was)?.label_en ?? '—'} → ${label(now)?.label_en ?? '—'}`,
                            undo: async () => { await actesUpdateDemande(a.id, { status_id: was }); await refresh(); },
                          });
                        });
                      }}>
                      {reference.acteStatuses.map((s) => (
                        <option key={s.id} value={s.id}>{lang === 'fr' ? s.label_fr : s.label_en}</option>
                      ))}
                    </select>
                    </span>
                  </td>
                  <td>
                    {/* The face IS the control: a photo, a full name and a
                        caret in one column were three things saying the same
                        one. Press the face to hand it to somebody else. */}
                    <OwnerPicker lang={lang === 'fr' ? 'fr' : 'en'} people={reference.profiles}
                      value={onActe.people(a)} canEdit={reference.me.isStaff && !busy}
                      onChange={(now) => {
                        /* La liste entière, avant et après : l'annulation remet
                           tout le monde, pas seulement la première personne. */
                        const was = onActe.emails(a);
                        const names = (list: string[]) => (list.length
                          ? list.map((e) => nameOf(reference.profiles.find((p) => p.email.toLowerCase() === e)
                            ?? { id: '', email: e, full_name: null, role: '', is_lawyer: false, is_active: false })).join(', ')
                          : '—');
                        void act(async () => {
                          await actesUpdateDemande(a.id, { assigned_emails: now });
                          pushUndo({
                            label: T('Responsables modifiés', 'Owners changed'),
                            detail: `${a.person_concerned} · ${names(was)} → ${names(now)}`,
                            undo: async () => { await actesUpdateDemande(a.id, { assigned_emails: was }); await refresh(); },
                          });
                        });
                      }} />
                  </td>
                  <td className="dim">{fmt(a.date_emission)}</td>
                  <td className={soon(a.date_expiration) ? 'warn' : 'dim'}>{fmt(a.date_expiration)}</td>
                  <td className="r">
                    <button className="actes-row-act" onClick={() => setViewActe(a.id)} title={T('Voir la demande', 'View the request')}>
                      <KBIcon name="eye" size={13} />
                    </button>
                    <button className="actes-row-act" onClick={() => setEditActe(a.id)} title={T('Modifier', 'Edit')}>
                      <KBIcon name="edit" size={13} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 5 and 6 — France only: Portugal has no DPC and no Poste log. */}
      {fr && (<>
      {/* 5 — Complementary pieces. */}
      <div className="sec-h">
        <h3>{T('Compléments (DPC)', 'Complementary pieces (DPC)')}</h3>
        {canWrite && (
          <div className="right">
            <button className="btn btn-s" onClick={() => setEditDpc('new')}>
              <KBIcon name="plus" size={13} /> {T('Nouvelle pièce complémentaire', 'New complementary piece')}
            </button>
          </div>
        )}
      </div>
      {/* Vide, c'est une ligne — pas une grande carte blanche pour dire « rien ». */}
      {dpc.length === 0 ? (
        <p className="sec-empty">{T('Aucune pièce complémentaire.', 'No complementary piece.')}</p>
      ) : (
        <div className="actes-block">
          <ul className="actes-list tight">
            {dpc.map((p) => (
              <li key={p.id}>
                <span className={`actes-when ${p.dpc_deadline_on && daysUntil(p.dpc_deadline_on) < 0 ? 'bad' : ''}`}
                  title={T('Date butoir', 'Deadline')}>
                  {fmt(p.dpc_deadline_on)}
                </span>
                <span className="actes-list-main">
                  <strong>{p.client_name}</strong>
                  <em>{p.status_label_fr || '—'}{p.request_number ? ` · ${p.request_number}` : ''}</em>
                </span>
                {p.file_path && (
                  <button className="actes-row-act" title={T('Voir le document', 'View the document')}
                    onClick={() => setView(() => () => actesDpcFile(p.id))}>
                    <KBIcon name="eye" size={13} />
                  </button>
                )}
                {canWrite && (
                  <>
                    <button className="actes-row-act" title={T('Modifier', 'Edit')} onClick={() => setEditDpc(p)}>
                      <KBIcon name="edit" size={13} />
                    </button>
                    <button className="actes-row-act danger" title={T('Supprimer', 'Delete')} onClick={() => setAskDpc(p)}>
                      <KBIcon name="trash" size={13} />
                    </button>
                  </>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {editDpc && (
        /* Locked to THIS dossier: a complementary piece opened from a dossier
           belongs to it, and asking which one again is asking a question whose
           answer is on screen. Its client case travels with it: without it
           the form asked to search for the case this dossier already carries. */
        <DpcModal lang={lang} reference={reference}
          dossiers={[{ id, name: d.name, ticket_id: d.ticket_id ?? null, hubspot_id: d.hubspot_id } as never]}
          lockDossier={id}
          initial={editDpc === 'new' ? null : editDpc}
          onClose={() => setEditDpc(null)} />
      )}

      {askDpc && (
        <div className="svc-modal-backdrop" onClick={() => setAskDpc(null)}>
          <div className="svc-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 420 }}>
            <div className="svc-modal-body">
              <h2>{T('Supprimer cette pièce ?', 'Delete this piece?')}</h2>
              <p className="muted-note">{T('Client', 'Client')}: <strong>{askDpc.client_name}</strong></p>
              <div className="nt-actions">
                <button className="btn btn-ghost" onClick={() => setAskDpc(null)}>{T('Annuler', 'Cancel')}</button>
                <button className="btn" disabled={busy}
                  onClick={() => void act(async () => { await actesDeleteDpc(askDpc.id); setAskDpc(null); })}>
                  {T('Supprimer', 'Delete')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 6 — The mail. */}
      <div className="sec-h">
        <h3>{T('Suivi de la Poste', 'Poste log')}</h3>
        {canWrite && (
          <div className="right">
            <button className="btn btn-s" disabled={busy} onClick={() => posteInput.current?.click()}>
              <KBIcon name="plus" size={13} /> {T('Ajouter un courrier', 'Add mail')}
            </button>
            <input ref={posteInput} type="file" hidden accept="application/pdf,image/png,image/jpeg,image/webp"
              onChange={(e) => {
                const f = e.target.files?.[0]; e.target.value = '';
                if (f) void act(async () => {
                  setPosteErr('');
                  try {
                    const up = await actesUpload(f, `${id}/_poste`);
                    await actesPosteAdd(id, { file_path: up.path, file_name: up.name, file_mime: up.mime });
                    await poste.refetch();
                  } catch (x) { setPosteErr(friendlyError(x, lang)); }
                });
              }} />
          </div>
        )}
      </div>
      {/* L'échec se dit ICI, sous le bouton. Il s'affichait en haut de la page,
          au-dessus des demandes : en bas, à côté de « Ajouter un courrier »,
          on ne voyait rien se passer — « la Poste ne marche pas ». */}
      {posteErr && <p className="muted-note ni-err" onClick={() => setPosteErr('')} role="status">⚠ {posteErr}</p>}
      {(poste.data ?? []).length === 0 ? (
        <p className="sec-empty">{T('Aucun courrier enregistré.', 'No mail recorded.')}</p>
      ) : (
        <div className="actes-block">
          <ul className="actes-list tight">
            {(poste.data ?? []).map((m) => (
              <li key={m.id}>
                <span className="actes-when">{new Date(m.received_at).toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-GB', { day: '2-digit', month: 'short' })}</span>
                <span className="actes-list-main">
                  <strong>{m.file_name || T('Courrier', 'Mail')}</strong>
                  <em>{[m.note, m.uploader_name].filter(Boolean).join(' · ')}</em>
                </span>
                <button className="actes-row-act" title={T('Voir', 'View')}
                  onClick={() => setView(() => () => actesPosteFile(id, m.id))}>
                  <KBIcon name="eye" size={13} />
                </button>
                {reference.me.isStaff && (
                  <button className="actes-row-act" title={T('Supprimer', 'Remove')}
                    onClick={() => void act(async () => { await actesPosteRemove(id, m.id); await poste.refetch(); })}>
                    <KBIcon name="trash" size={13} />
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
      </>)}

      {/* 7 — What people said about it. Tagging somebody turns a note into a
          small task: they are notified and come to read it. And a note posted
          by mistake can be taken back. */}
      <div className="sec-h"><h3>{T('Notes', 'Notes')}</h3></div>
      <div className="actes-block">
        <Notes lang={lang} reference={reference} dossierId={id} />
      </div>

      {/* 8 — Everything that happened, on demand. */}
      {showHistory && (
        <>
          <div className="sec-h"><h3>{T('Historique', 'History')}</h3></div>
          <div className="actes-block">
            {history.isLoading ? <p className="muted-note">{T('Chargement…', 'Loading…')}</p> : (
              <ul className="actes-list tight">
                {(history.data?.log ?? []).slice(0, 60).map((l) => (
                  <li key={l.id}>
                    <span className="actes-when">{new Date(l.created_at).toLocaleDateString()}</span>
                    <span className="actes-list-main">
                      <strong>{l.action}{l.field ? ` · ${l.field}` : ''}</strong>
                      <em>{[l.old_value, l.new_value].filter(Boolean).join(' → ')}{l.actor_name ? ` — ${l.actor_name}` : ''}</em>
                    </span>
                  </li>
                ))}
                {(history.data?.log ?? []).length === 0 && <p className="muted-note">{T('Rien encore.', 'Nothing yet.')}</p>}
              </ul>
            )}
          </div>
        </>
      )}

      {/* ── The two questions that need a real answer before acting ── */}
      {reason && (
        <div className="svc-modal-backdrop" onClick={() => setReason(null)}>
          <div className="svc-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 460 }}>
            <div className="svc-modal-body">
              <h2>{reason.what === 'close' ? T('Clôturer le dossier', 'Close the dossier') : T('Rouvrir le dossier', 'Reopen the dossier')}</h2>
              <label className="actes-field">
                <span>{T('Motif (obligatoire)', 'Reason (required)')}</span>
                <textarea rows={3} value={reason.text} autoFocus
                  onChange={(e) => setReason({ ...reason, text: e.target.value })} />
              </label>
              <p className="muted-note">
                {T('Le motif est enregistré dans l’historique et envoyé à l’équipe du dossier.',
                  'The reason is written to the history and sent to the dossier’s team.')}
              </p>
              <div className="nt-actions">
                <button className="btn btn-ghost" onClick={() => setReason(null)}>{T('Annuler', 'Cancel')}</button>
                <button className="btn" disabled={!reason.text.trim() || busy}
                  onClick={() => void act(async () => { await actesDossierLifecycle(id, reason.what, reason.text.trim()); setReason(null); })}>
                  {reason.what === 'close' ? T('Clôturer', 'Close') : T('Rouvrir', 'Reopen')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {zipAsk !== null && (
        <div className="svc-modal-backdrop" onClick={() => setZipAsk(null)}>
          <div className="svc-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 420 }}>
            <div className="svc-modal-body">
              <h2>{T('Télécharger les documents', 'Download the documents')}</h2>
              <p className="muted-note">
                {zipAsk === 0
                  ? T('Ce dossier ne contient aucun document reçu.', 'This dossier holds no received document.')
                  : T(`${zipAsk} documents seront réunis dans une archive, nommés d’après la personne concernée.`,
                    `${zipAsk} documents will be gathered into one archive, named after the person concerned.`)}
              </p>
              <div className="nt-actions">
                <button className="btn btn-ghost" onClick={() => setZipAsk(null)}>{T('Annuler', 'Cancel')}</button>
                <button className="btn" disabled={!zipAsk || busy}
                  onClick={() => void act(async () => { await actesAttachmentsZip(id, d.name); setZipAsk(null); })}>
                  {T('Télécharger', 'Download')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {completing && <ParallelComplementModal lang={lang} reference={reference} dossierId={id}
        parallelId={fr ? undefined : tk.data?.parallelId ?? null} onClose={() => setCompleting(false)} />}
      {editing && <DossierModal lang={lang} reference={reference} initial={d} serviceIds={q.data.services} onClose={() => setEditing(false)} />}
      {(adding || editActe || seed) && (
        <ActeModal lang={lang} reference={reference} dossierId={id}
          initial={editActe ? actes.find((a) => a.id === editActe) ?? null : null}
          seed={seed}
          onClose={() => { setAdding(false); setEditActe(null); setSeed(null); }} />
      )}
    </section>
  );
}
